import { useCallback, useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * PWA Update Hook
 * 
 * Manages service worker updates using vite-plugin-pwa's React integration.
 * - Detects when a new version is available (needRefresh)
 * - Provides an update function to reload with the new version
 * - Logs lifecycle events for debugging
 * 
 * How it works:
 * 1. useRegisterSW registers the service worker with immediate: true
 * 2. When a new SW version is detected, onNeedRefresh is called
 * 3. needRefresh becomes true, triggering the update dialog
 * 4. User clicks "Reload Now" -> updateServiceWorker(true) is called
 * 5. New SW activates and page reloads with the latest version
 * 
 * Only active in production builds where service workers are registered.
 * In development, the hook returns safe defaults (needRefresh: false).
 */

interface UsePWAUpdateOptions {
  /** Enable console debug logs (default: true in dev, false in prod) */
  debug?: boolean;
}

interface UsePWAUpdateReturn {
  /** True when a new service worker is waiting to activate */
  needRefresh: boolean;
  /** Trigger the update - activates new SW and reloads the page */
  update: () => void;
  /** True if app is ready to work offline */
  offlineReady: boolean;
  /** True if service workers are supported in this browser */
  isSupported: boolean;
}

export function usePWAUpdate(options: UsePWAUpdateOptions = {}): UsePWAUpdateReturn {
  // const { debug = import.meta.env.DEV } = options;
  const { debug = true } = options;
  
  // Check if service workers are supported
  const isSupported = typeof window !== 'undefined' && 'serviceWorker' in navigator;

  const log = useCallback(
    (message: string, ...args: unknown[]) => {
      if (debug) {
        console.log(`[PWA] ${message}`, ...args);
      }
    },
    [debug]
  );

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegistered(registration) {
      log('Service worker registered', registration);
    },
    onRegisterError(error) {
      log('Service worker registration error', error);
    },
    onNeedRefresh() {
      log('New version available');
    },
    onOfflineReady() {
      log('App ready offline');
    },
  });

  // Log state changes
  useEffect(() => {
    if (offlineReady) {
      log('App ready offline');
    }
  }, [offlineReady, log]);

  useEffect(() => {
    if (needRefresh) {
      log('(useEffect) New version available');
    }
  }, [needRefresh, log]);

  const update = useCallback(() => {
    log('Updating to new version...');
    updateServiceWorker(true);
  }, [updateServiceWorker, log]);

  return {
    // Only show update dialog if SW is supported and update is available
    needRefresh: isSupported && needRefresh,
    update,
    offlineReady,
    isSupported,
  };
}

export default usePWAUpdate;
